// C9 — Resumen semanal exportable.
//
// Refactor visual a detalle (sesión 2026-05-28) + gráfico semanal y semáforo
// unificado (sesión 2026-06-07):
//   - Header consistente (título + subtítulo informativo + botones top-right)
//   - Tokens semánticos (text-fg / text-fg-tertiary / text-fg-secondary, sin colores hardcoded)
//   - Loading/error states alineados con el resto
//   - Semáforo de cumplimiento unificado a 90/70 vía lib/semaforo (igual que C5)
//   - Gráfico "Evolución de la semana" (ComposedChart Recharts: barras de cantidad
//     real + línea de cumplimiento vs meta)
//   - Top líneas peor cumplimiento clicable → drill a /coordinador/linea/:id con
//     contexto temporal de la semana vista
//
// Estado original — vista compacta para presentar a directivos:
//   - 7 KPIs con comparativo vs semana anterior (delta + flecha)
//   - Top 5 líneas peor cumplimiento
//   - Top 5 causales de paro
//   - Breakdown por planta
//   - Alertas de la semana
// Export CSV via /api/coordinador/resumen-semanal.csv (BOM para Excel).
//
// Coherencia con el diccionario v1 (sesión 2026-09-09):
//   - Semana en la URL (?p=semana&ancla=) vía usePeriodo — ◀ ▶ y enlace compartible.
//   - DN8: la semana en curso se compara a la misma altura de la anterior.
//   - A6: el header declara la base "producción capturada · X % pendiente de validar".
//   - B1 como KPI principal de paro + chip DN1 "N en curso"; B2 como texto secundario.
//   - Sin rojo/ámbar decorativo: el color solo lo pone el semáforo de cumplimiento.

import { useMemo, useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EtiquetaValor } from '../../components/charts/value-labels';
import type { KpiComparativo, ResumenSemanalQuery, ResumenSerieDiariaPunto } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { useCoordinadorResumenSemanal } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { PeriodoNav } from '../../lib/periodo/PeriodoNav';
import { usePeriodo } from '../../lib/periodo/periodo';
import { REGLA_SEMAFORO_OPERACION, semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';
import { supabase, API_URL } from '../../lib/supabase';
import { cn } from '../../lib/utils';
import { etiquetaBaseProduccion, etiquetaComparativoSemana, kpiMinutosAHoras } from './c9Etiquetas';

// =============================================================================
// Helpers de formato (es-CO)
// =============================================================================

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

export function C9ResumenSemanalPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  // Semana en la URL (?p=semana&ancla=YYYY-MM-DD). El API recibe cualquier día
  // de la semana en `semana=` y resuelve el inicio operativo; le mandamos el
  // inicio que ya derivó el hook para que la clave de caché sea estable.
  const per = usePeriodo(undefined, 'semana', { url: true });
  const filtros = useMemo<ResumenSemanalQuery>(
    () => ({ semana: per.periodo.desde }),
    [per.periodo.desde],
  );
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, error } = useCoordinadorResumenSemanal(filtros);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando resumen…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  const descargarCsv = async () => {
    setExporting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        toast.error('Sesión expirada');
        return;
      }
      const params = new URLSearchParams();
      if (filtros.semana) params.set('semana', filtros.semana);
      const qs = params.toString();
      const res = await fetch(
        `${API_URL}/api/coordinador/resumen-semanal.csv${qs ? `?${qs}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        toast.error(`Error ${res.status}: no se pudo exportar`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resumen-semanal-${data.semana.desde}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setExporting(false);
    }
  };

  const base = etiquetaBaseProduccion(data.pendiente_validacion, data.kpis.cantidad_real.actual);
  const comparativo = etiquetaComparativoSemana(data.semana);
  const paroDispHoras = kpiMinutosAHoras(data.kpis.paro_disponibilidad_min);

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Resumen semanal</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {data.tenant.nombre} · {data.semana.desde} a {data.semana.hasta}
              {data.semana.parcial ? ` · parcial · ${data.semana.dias} de 7 días` : ''} ·{' '}
              {comparativo} ({data.semana.desde_anterior} a {data.semana.hasta_anterior})
            </p>
            <p className="mt-1 inline-flex flex-wrap items-center gap-1.5 text-[11px] text-fg-tertiary">
              producción capturada ·
              {base.tone === 'neutral' ? (
                <span>{base.texto}</span>
              ) : (
                <Badge tone={base.tone}>{base.texto}</Badge>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PeriodoNav
              label={per.periodo.label}
              isCurrent={per.periodo.isCurrent}
              canNext={!per.periodo.isCurrent}
              onPrev={() => per.shiftAnchor(-1)}
              onNext={() => per.shiftAnchor(1)}
              onReset={per.resetPeriodo}
            />
            <Button onClick={descargarCsv} loading={exporting} variant="primary">
              Descargar CSV
            </Button>
          </div>
        </header>

        {data.alertas.length > 0 && (
          <section>
            <h2 className="mb-2 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Alertas
            </h2>
            <div className="flex flex-col gap-2">
              {data.alertas.map((a, i) => (
                <Card key={i} variant={a.severidad === 'alta' ? 'crit-left' : 'warn-left'}>
                  <CardContent className="px-4 py-3 text-sm">
                    <span
                      className={cn(
                        'mr-2 inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                        a.severidad === 'alta' ? 'bg-crit/10 text-crit' : 'bg-warn/10 text-warn',
                      )}
                    >
                      {a.severidad}
                    </span>
                    <span className="text-fg">{a.mensaje}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            KPIs principales
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <KpiTile
              label="Cumplimiento"
              k={data.kpis.cumplimiento_pct}
              suffix="%"
              toneOnPos="ok"
              comparativo={comparativo}
            />
            <KpiTile
              label="Cantidad real"
              k={data.kpis.cantidad_real}
              format="int"
              toneOnPos="ok"
              comparativo={comparativo}
            />
            <KpiTile
              label="Cantidad programa"
              k={data.kpis.cantidad_programa}
              format="int"
              toneOnPos="neutral"
              comparativo={comparativo}
            />
            <KpiTile
              label="Paro que afecta disponibilidad"
              k={paroDispHoras}
              suffix="h"
              format="dec1"
              toneOnPos="crit"
              comparativo={comparativo}
              extra={
                data.paros_en_curso > 0 ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-warn px-2 py-px text-[10.5px] font-semibold text-warn"
                    title="Paros abiertos ahora: se cuentan aparte hasta que cierren (DN1)"
                  >
                    <span className="size-1.5 animate-pulse rounded-full bg-warn" />
                    {data.paros_en_curso} en curso — aparte, no suman
                  </span>
                ) : null
              }
              nota={`Paro total registrado (incl. programados): ${data.kpis.horas_paro.actual.toFixed(1)} h`}
            />
            <KpiTile
              label="Líneas con captura"
              k={data.kpis.lineas_con_captura}
              format="int"
              toneOnPos="ok"
              comparativo={comparativo}
            />
            <KpiTile
              label="Bloques capturados"
              k={data.kpis.bloques_capturados}
              format="int"
              toneOnPos="ok"
              comparativo={comparativo}
            />
            <KpiTile
              label="Acciones supervisión"
              k={data.kpis.acciones_supervision}
              format="int"
              toneOnPos="neutral"
              comparativo={comparativo}
            />
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Evolución de la semana
          </h2>
          <SerieDiariaChart serie={data.serie_diaria} />
        </section>

        <section>
          <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Por planta ({data.por_planta.length}){' '}
            <span className="normal-case tracking-normal text-fg-tertiary/70">
              · semáforo de cumplimiento: {REGLA_SEMAFORO_OPERACION}
            </span>
          </h2>
          {data.por_planta.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                Sin plantas asignadas.
              </CardContent>
            </Card>
          ) : (
            <Table maxHeight="60vh">
              <THead sticky>
                <TR>
                  <TH>Planta</TH>
                  <TH align="right">Cumpl.</TH>
                  <TH align="right">Real</TH>
                  <TH align="right">Programa</TH>
                  <TH align="right">Paro disp. (h)</TH>
                  <TH align="right">Paro total (h)</TH>
                  <TH align="right">Líneas activas</TH>
                </TR>
              </THead>
              <TBody>
                {data.por_planta.map((p) => (
                  <TR key={p.planta_id}>
                    <TD>
                      <span className="font-medium text-fg">{p.planta_codigo}</span>{' '}
                      <span className="text-xs text-fg-tertiary">{p.planta_nombre}</span>
                    </TD>
                    <TD align="right" numeric>
                      {p.cumplimiento_pct !== null ? (
                        <span
                          className={cn(
                            'font-medium',
                            toneTextClass(semaforoCumplimiento(p.cumplimiento_pct)),
                          )}
                        >
                          {p.cumplimiento_pct.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-fg-tertiary">—</span>
                      )}
                    </TD>
                    <TD align="right" numeric className="text-fg">
                      {Math.round(p.cantidad_real).toLocaleString('es-CO')}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {Math.round(p.cantidad_programa).toLocaleString('es-CO')}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {(p.paro_disponibilidad_min / 60).toFixed(1)}
                      {p.paros_en_curso > 0 ? (
                        <span
                          className="ml-1 text-[10px] text-fg-tertiary"
                          title="Paros abiertos: aparte, no suman (DN1)"
                        >
                          · {p.paros_en_curso} en curso
                        </span>
                      ) : null}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {p.horas_paro.toFixed(1)}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {p.lineas_con_captura} / {p.lineas_total}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <section>
            <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Top líneas peor cumplimiento{' '}
              <span className="text-fg-tertiary/70">· vs capacidad</span>{' '}
              <span className="normal-case tracking-normal text-fg-tertiary/70">
                · {REGLA_SEMAFORO_OPERACION}
              </span>
            </h2>
            {data.top_lineas_peor_cumplimiento.length === 0 ? (
              <Card>
                <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                  Sin datos.
                </CardContent>
              </Card>
            ) : (
              <Table maxHeight="60vh">
                <THead sticky>
                  <TR>
                    <TH>Línea</TH>
                    <TH align="right">Cumpl.</TH>
                    <TH align="right">Sin producir</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.top_lineas_peor_cumplimiento.map((l) => {
                    const irADetalle = () => {
                      // Propagar el período de la semana vista a "Línea en el tiempo".
                      const params = new URLSearchParams();
                      params.set('desde', data.semana.desde);
                      params.set('hasta', data.semana.hasta);
                      params.set('granularidad', 'dia');
                      navigate(`/coordinador/linea/${l.linea_id}?${params.toString()}`);
                    };
                    return (
                      <TR
                        key={l.linea_id}
                        className="cursor-pointer transition-colors hover:bg-surface-overlay/40"
                        role="button"
                        tabIndex={0}
                        onClick={irADetalle}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            irADetalle();
                          }
                        }}
                      >
                        <TD>
                          <span className="font-medium text-fg">{l.linea_codigo}</span>
                          <div className="text-xs text-fg-tertiary">
                            {l.planta_nombre} · {l.linea_nombre}
                          </div>
                        </TD>
                        <TD align="right" numeric>
                          {l.cumplimiento_meta_pct !== null ? (
                            <span
                              className={cn(
                                'font-medium',
                                toneTextClass(semaforoCumplimiento(l.cumplimiento_meta_pct)),
                              )}
                            >
                              {l.cumplimiento_meta_pct.toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-fg-tertiary">—</span>
                          )}
                        </TD>
                        <TD align="right" numeric className="text-fg-secondary">
                          {Math.round(l.unidades_sin_producir).toLocaleString('es-CO')}
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </section>

          <section>
            <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Top causales de paro{' '}
              <span className="normal-case tracking-normal text-fg-tertiary/70">
                · paro total registrado, solo eventos cerrados
              </span>
            </h2>
            {data.top_paros.length === 0 ? (
              <Card>
                <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                  Sin paros registrados.
                </CardContent>
              </Card>
            ) : (
              <Table maxHeight="60vh">
                <THead sticky>
                  <TR>
                    <TH>Causal</TH>
                    <TH align="right">Horas</TH>
                    <TH align="right"># Paros</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.top_paros.map((c) => (
                    <TR key={c.causal_id}>
                      <TD>
                        <span className="font-medium text-fg">{c.causal_nombre}</span>
                        {c.causal_madre_nombre && (
                          <div className="text-xs text-fg-tertiary">{c.causal_madre_nombre}</div>
                        )}
                      </TD>
                      <TD align="right" numeric className="font-medium text-fg-secondary">
                        {c.horas_total.toFixed(1)}
                      </TD>
                      <TD align="right" numeric className="text-fg-secondary">
                        {c.paros_count}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </section>
        </div>
      </div>
    </RoleLayout>
  );
}

// =============================================================================

/** Gráfico "Evolución de la semana": barras de cantidad real (eje izq) + línea
 *  de cumplimiento vs meta (eje der 0-100). Empty state si no hay días. */
function SerieDiariaChart({ serie }: { serie: ResumenSerieDiariaPunto[] }) {
  if (serie.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-fg-tertiary">
          Sin datos en la semana.
        </CardContent>
      </Card>
    );
  }

  const chartData = serie.map((p) => ({
    label: formatearFechaCorta(p.fecha),
    cantidad_real: p.cantidad_real,
    cumplimiento_meta_pct: p.cumplimiento_meta_pct,
    horas_paro: p.horas_paro,
  }));

  return (
    <Card>
      <CardContent className="p-3">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 16, right: 12, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                width={48}
                tickFormatter={(v: number) => formatearMiles(v)}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                width={40}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: 'var(--fg-secondary)' }}
                formatter={(value, name) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  const label = String(name);
                  if (value === null || value === undefined || Number.isNaN(n)) {
                    return ['—', label];
                  }
                  if (label === 'Cumplimiento meta') return [`${n.toFixed(0)}%`, label];
                  return [formatearMiles(n), label];
                }}
              />
              <Bar
                yAxisId="left"
                dataKey="cantidad_real"
                name="Cantidad real"
                fill="var(--accent)"
                radius={[3, 3, 0, 0]}
                maxBarSize={48}
              >
                <LabelList
                  content={
                    <EtiquetaValor
                      formatter={(v) => formatearMiles(v)}
                      total={chartData.length}
                      fill="var(--fg-secondary)"
                    />
                  }
                />
              </Bar>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cumplimiento_meta_pct"
                name="Cumplimiento meta"
                stroke="var(--ok)"
                strokeWidth={2}
                dot={{ r: 3, fill: 'var(--ok)' }}
                activeDot={{ r: 5 }}
                connectNulls
              >
                <LabelList
                  content={
                    <EtiquetaValor
                      formatter={(v) => `${v.toFixed(0)}%`}
                      total={chartData.length}
                      dy={-10}
                      fill="var(--fg-secondary)"
                    />
                  }
                />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================

type Tone = 'ok' | 'crit' | 'neutral';

function KpiTile({
  label,
  k,
  suffix = '',
  format = 'dec1',
  toneOnPos,
  comparativo = 'vs sem. ant.',
  extra,
  nota,
}: {
  label: string;
  k: KpiComparativo;
  suffix?: string;
  format?: 'int' | 'dec1' | 'dec2';
  /** Si delta > 0, ¿es bueno (ok), malo (crit) o neutro? Invierte cuando es < 0. */
  toneOnPos: Tone;
  /** Texto del comparativo (DN8: misma altura cuando la semana está en curso). */
  comparativo?: string;
  /** Chip junto al valor (p. ej. DN1 "N en curso"). */
  extra?: ReactNode;
  /** Nota secundaria bajo el delta (p. ej. B2 paro total registrado). */
  nota?: ReactNode;
}) {
  const fmt = (n: number) => {
    if (format === 'int') return Math.round(n).toLocaleString('es-CO');
    if (format === 'dec1') return n.toFixed(1);
    return n.toFixed(2);
  };

  const sign = k.delta_abs > 0 ? '+' : k.delta_abs < 0 ? '' : '±';
  const arrow = k.delta_abs > 0 ? '↑' : k.delta_abs < 0 ? '↓' : '·';

  // Color del delta — depende del signo y de si "positivo" es bueno o malo para este KPI.
  let deltaClass = 'text-fg-tertiary';
  if (k.delta_abs !== 0 && toneOnPos !== 'neutral') {
    const positivoEsBueno = toneOnPos === 'ok';
    const esBueno = (k.delta_abs > 0 && positivoEsBueno) || (k.delta_abs < 0 && !positivoEsBueno);
    deltaClass = esBueno ? 'text-ok' : 'text-crit';
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4 pt-4">
        <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          {label}
        </span>
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-[32px] font-medium leading-none text-fg"
            style={{ letterSpacing: '-1.2px', fontVariantNumeric: 'tabular-nums' }}
          >
            {fmt(k.actual)}
          </span>
          {suffix ? (
            <span className="text-[13px] tracking-tight text-fg-secondary">{suffix}</span>
          ) : null}
          {extra}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('font-medium', deltaClass)}>
            {arrow} {sign}
            {fmt(Math.abs(k.delta_abs))}
            {suffix}
          </span>
          {k.delta_pct !== null && (
            <span className={cn(deltaClass)}>
              ({k.delta_pct > 0 ? '+' : ''}
              {k.delta_pct.toFixed(0)}%)
            </span>
          )}
          <span className="text-fg-tertiary">{comparativo}</span>
        </div>
        {nota ? <div className="text-[11px] text-fg-tertiary">{nota}</div> : null}
      </CardContent>
    </Card>
  );
}
